import type { MemoSourceType, MemoTemplate } from './schema.js'

/**
 * The kinds of candidate a memo's own text groups its items into, rendered in
 * this order within every block - see `itemsOfBlock` in `renderMemo.ts`.
 *
 * A published article is TT's own material just like a planning is - often the
 * very same story - while a calendar event is somebody else's, and reads
 * differently in a memo. A print article is what is actually going into the
 * paper, and is kept apart from the material a story is planned as: the same
 * story can well be both, and a Print-PM wants it as the print article it
 * ended up as.
 *
 * This is a rendering concept, not a picking one - see `MemoCandidateColumn`
 * below for how the creation dialog splits candidates for browsing and
 * selection, which is a finer split than a memo's own text bothers with.
 */
export const memoColumns = ['print', 'material', 'events'] as const

export type MemoColumn = (typeof memoColumns)[number]

export const memoColumnOrder: MemoColumn[] = [...memoColumns]

export function memoColumn(sourceType: MemoSourceType): MemoColumn {
  if (sourceType === 'print-article') {
    return 'print'
  }

  if (sourceType === 'event') {
    return 'events'
  }

  return 'material'
}

/**
 * The columns the memo creation dialog splits candidates into for browsing,
 * filtering and selection - one per source type, since an editor picks
 * plannings and articles independently of one another even where a memo
 * prints them as one "material" run - see `MemoColumn` above.
 *
 * Distinct from `MemoColumn`: two produced-article candidates that would
 * collide in a memo's own material run stay apart here, each pickable on its
 * own, in the section for what it is rather than nested under or merged with
 * whatever produced it.
 */
export const memoCandidateColumns = ['print', 'planning', 'article', 'events'] as const

export type MemoCandidateColumn = (typeof memoCandidateColumns)[number]

export const memoCandidateColumnOrder: MemoCandidateColumn[] = [...memoCandidateColumns]

export function memoCandidateColumn(sourceType: MemoSourceType): MemoCandidateColumn {
  if (sourceType === 'print-article') {
    return 'print'
  }

  if (sourceType === 'event') {
    return 'events'
  }

  return sourceType
}

/** Whether the template gives each candidate column a date range of its own. */
export function hasColumnDates(template: MemoTemplate): boolean {
  return template.sources.some((source) => source.leadDays !== undefined)
}

/** How far ahead a column's range opens: its sources' own lead, else the template's. */
export function columnLeadDays(template: MemoTemplate, column: MemoCandidateColumn): number {
  const source = template.sources.find((candidate) => (
    memoCandidateColumn(candidate.type) === column && candidate.leadDays !== undefined
  ))

  return source?.leadDays ?? template.leadDays
}
